import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { iconDelete } from '@/components/icons';
import { ANIMATION_SPEED, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import {
  animationsComplete,
  findFirstFocusableRecursive,
  getRevealedAssignedSlotElement,
  isSafari,
} from '@/utils';

/**
 * @element fg-dialog
 */
@customElement('fg-dialog')
@localized()
export class FgDialog extends LitElement {
  private dialogRef: Ref<HTMLDialogElement> = createRef();

  private closeButtonRef: Ref<HTMLButtonElement> = createRef();

  @property({ type: Boolean })
  fullscreen = false;

  @property({ type: Boolean })
  compact = false;

  @property({ type: Boolean })
  hasAccessibleFocusOnContent = false;

  onDialogKeyDown = (event: KeyboardEvent) => {
    if (event.key == 'Escape') {
      this.close();
    }
  };

  private slotObserver: MutationObserver | null = null;

  async firstUpdated() {
    const dialog = this.dialogRef.value;

    if (dialog !== undefined) {
      await animationsComplete(dialog);
      dialog.removeAttribute('loading');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.hasAccessibleFocusOnContent && this.slotObserver) {
      this.slotObserver.disconnect();
      this.slotObserver = null;
    }
  }

  private setupSlotObserver() {
    if (this.slotObserver) {
      this.slotObserver.disconnect();
      this.slotObserver = null;
    }

    const dialog = this.dialogRef.value;
    if (!dialog) return;

    const slottedElement = getRevealedAssignedSlotElement(
      this.dialogRef.value,
      'article slot',
    );

    if (!slottedElement) {
      return;
    }

    this.slotObserver = new MutationObserver(() => {
      setTimeout(() => {
        this.handleSlotChange();
      }, 0);
    });

    this.slotObserver.observe(slottedElement, {
      childList: true,
      attributes: false,
      characterData: false,
      subtree: true,
    });
  }

  private async handleSlotChange() {
    const slottedElement = getRevealedAssignedSlotElement(
      this.dialogRef.value,
      'article slot',
    );

    if (!slottedElement) {
      return;
    }

    try {
      const focusableElement =
        await findFirstFocusableRecursive(slottedElement);

      if (focusableElement) {
        focusableElement.focus();
      } else {
        this.closeButtonRef.value?.focus();
      }
    } catch (error) {
      console.error('Dialog: Error handling slot change:', error);
    }
  }

  public async open() {
    const dialog = this.dialogRef.value;
    window.addEventListener('keydown', this.onDialogKeyDown);

    if (dialog === undefined) {
      return;
    }

    dialog.removeAttribute('inert');
    dialog.showModal();

    if (this.hasAccessibleFocusOnContent) {
      this.setupSlotObserver();
      await this.handleSlotChange();
    }
  }

  public close() {
    if (this.hasAccessibleFocusOnContent && this.slotObserver) {
      this.slotObserver.disconnect();
      this.slotObserver = null;
    }

    window.removeEventListener('keydown', this.onDialogKeyDown);
    this.dialogRef.value?.setAttribute('inert', '');
    this.dialogRef.value?.close('close');
    this.dispatchEvent(new Event('dialog-closed'));
  }

  private clickOutsideDismiss(event: Event) {
    const dialog = this.dialogRef.value;

    if (dialog !== undefined && event.target === dialog) {
      this.close();
    }
  }

  render() {
    const dialogRefTemplate = ref(this.dialogRef),
      closeButtonRefTemplate = ref(this.closeButtonRef),
      dialogCssClasses = {
        fullscreen: this.fullscreen,
        'max-content': isSafari(),
        compact: this.compact,
      };

    return html`
      <dialog
        ${dialogRefTemplate}
        inert
        loading
        @click=${this.clickOutsideDismiss}
        class=${classMap(dialogCssClasses)}
      >
        <form method="dialog">
          <header>
            <div>
              <h1><slot name="title"></slot></h1>
              <button
                ${closeButtonRefTemplate}
                @click=${this.close}
                type="button"
                .title=${t('actions.closeDialog') || ''}
                class="close-button"
              >
                <span aria-hidden="true">${iconDelete}</span>
              </button>
            </div>
            <span><slot name="pre-body"></slot></span>
          </header>
          <article>
            <slot></slot>
          </article>
          ${!this.fullscreen
            ? html`
                <footer>
                  <slot name="footer"></slot>
                </footer>
              `
            : nothing}
        </form>
      </dialog>
    `;
  }

  static styles = [
    baseStyles,
    css`
      dialog {
        background: white;
        border: 0;
        box-shadow: var(--fg-boxshadow--xl, var(--fg-fallback-boxshadow--xl));
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        display: grid;
        inset: 0;
        overflow: hidden;
        margin: auto;
        margin-bottom: 0;
        max-width: 100vw;
        padding: 0;
        position: fixed;
        transition: opacity ${ANIMATION_SPEED}s;
        width: 100vw;
      }

      dialog[loading] {
        visibility: hidden;
      }

      dialog[open] {
        animation: slide-in-up ${ANIMATION_SPEED}s cubic-bezier(0, 0, 0.3, 1);
      }

      dialog:not([open]) {
        animation: slide-out-down ${ANIMATION_SPEED}s ease-in;
        display: grid;
        opacity: 0;
        pointer-events: none;
      }

      dialog::backdrop {
        background: rgba(0, 0, 0, 0.5);
      }

      dialog + .backdrop {
        /* polyfill */
        background: rgba(0, 0, 0, 0.5);
        height: 100%;
        left: 0;
        position: fixed;
        top: 0;
        width: 100%;
      }

      dialog.max-content {
        height: auto;
        max-height: max-content;
      }

      dialog form {
        align-items: start;
        display: grid;
        grid-template-rows: auto 1fr auto;
        max-block-size: 90vh;
        padding: 20px 15px 30px;
      }

      header {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        padding-bottom: 25px;
        position: relative;
      }

      header h1 {
        font-size: 22px;
        margin: 0;
      }

      header > div {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }

      .close-button svg {
        height: 16px;
        width: 17px;
        color: #000000;
      }

      :host([part='wholesale']) header h1 {
        color: var(--fg-primary, var(--fg-fallback-primary));
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
        font-weight: 600;
      }

      :host([part='wholesale']) header button {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      header button {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        flex-shrink: 0;
        display: flex;
        align-items: center;
        height: 16px;
        margin-top: 3px;
      }

      header span {
        width: 100%;
      }

      article {
        max-block-size: 100%; /* safari */
        overscroll-behavior-y: contain;
        overflow-y: auto;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        dialog {
          border-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
          margin-block-end: auto;
          max-inline-size: min(90vw, 890px);
          width: auto;
        }

        :host([part='wholesale']) dialog {
          max-inline-size: min(90vw, 890px);
        }

        :host([part='wholesale']) dialog.compact {
          max-inline-size: min(90vw, 540px);
        }

        :host([part='wholesale']) dialog.compact header {
          padding-bottom: 12px;
        }

        dialog[open] {
          animation: slide-in-up ${ANIMATION_SPEED}s
            cubic-bezier(0.5, -0.1, 0.1, 1.2);
        }

        dialog form {
          padding: 32px;
        }

        header {
          padding-bottom: 0;
        }

        header h1 {
          font-size: 28px;
          padding-bottom: 24px;
        }

        article {
          padding-bottom: 24px;
        }

        dialog.fullscreen {
          animation: none;
          border-radius: 0;
          height: 100vh;
          margin: 0;
          max-height: 100vh;
          max-inline-size: 100vw;
          transition: none;
        }

        dialog.fullscreen form {
          max-block-size: 100vh;
          padding: 20px;
        }

        dialog.fullscreen header {
          padding: 0;
        }

        dialog.fullscreen article {
          height: 100%;
        }
      }

      @keyframes slide-in-up {
        from {
          transform: translateY(100%);
        }
      }

      @keyframes slide-out-down {
        to {
          transform: translateY(100%);
        }
      }
    `,
  ];
}
